import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, Phone, Mail } from "lucide-react";
import { Logo } from "@/components/Logo";

const EASE = [0.76, 0, 0.24, 1];

export const Navbar = ({ ready = true, dark = false }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const goSection = (id) => {
    if (location.pathname === "/") {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/", { state: { scrollTo: id } });
    }
  };

  const LINKS = [
    { label: "Concepts", action: () => navigate("/concepts") },
    { label: "Services", action: () => goSection("services") },
    { label: "Journal", action: () => navigate("/blogs") },
    { label: "Case Studies", action: () => navigate("/case-studies") },
    { label: "Contact", action: () => goSection("enquiry") },
  ];

  const runLink = (action) => {
    setMenuOpen(false);
    setTimeout(action, 50);
  };

  const onDark = dark && !scrolled;
  const textCol = onDark ? "text-[#FDFBF7]" : "text-[#1A1713]";

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={ready ? { y: 0, opacity: 1 } : {}}
        transition={{ duration: 1, ease: EASE, delay: 0.2 }}
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
          scrolled
            ? "bg-[#FDFBF7]/85 backdrop-blur-md border-b border-[#E8E4D9]"
            : "bg-transparent"
        }`}
        data-testid="navbar"
      >
        <nav className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 h-20 flex items-center justify-between gap-2">
          <button
            onClick={() => navigate("/")}
            className={`flex items-center gap-2 sm:gap-3 min-w-0 ${textCol}`}
            data-testid="nav-logo"
          >
            <Logo size={36} />
            <span className="font-serif text-sm sm:text-xl tracking-[0.15em] whitespace-nowrap">AURELUME</span>
          </button>

          <div className="hidden lg:flex items-center gap-9">
            {LINKS.map((l) => (
              <button
                key={l.label}
                onClick={l.action}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`text-xs uppercase tracking-[0.2em] ${textCol} opacity-70 hover:opacity-100 link-underline`}
              >
                {l.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={() => navigate("/request-concept")}
              data-testid="nav-request-concept"
              className="text-[9px] sm:text-[11px] uppercase tracking-[0.12em] sm:tracking-[0.18em] bg-[#C5A059] text-[#1A1713] px-3 py-2.5 sm:px-5 sm:py-3 whitespace-nowrap hover:bg-[#1A1713] hover:text-[#FDFBF7] transition-colors duration-400 font-medium"
            >
              Request a Concept
            </button>
            <button
              onClick={() => setMenuOpen(true)}
              data-testid="nav-menu-toggle"
              aria-label="Open menu"
              className={`lg:hidden w-10 h-10 flex items-center justify-center ${textCol}`}
            >
              <Menu size={24} strokeWidth={1.5} />
            </button>
          </div>
        </nav>
      </motion.header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="fixed inset-0 z-[90] bg-[#1A1713] text-[#FDFBF7] flex flex-col lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="px-4 sm:px-6 h-20 flex items-center justify-between border-b border-white/10">
              <div className="flex items-center gap-2 text-[#FDFBF7]">
                <Logo size={36} />
                <span className="font-serif text-sm sm:text-xl tracking-[0.15em]">AURELUME</span>
              </div>
              <button
                onClick={() => setMenuOpen(false)}
                data-testid="mobile-menu-close"
                aria-label="Close menu"
                className="w-10 h-10 flex items-center justify-center text-[#FDFBF7]"
              >
                <X size={26} strokeWidth={1.5} />
              </button>
            </div>

            <div className="flex-1 flex flex-col justify-center px-6 sm:px-10 overflow-y-auto">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.label}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + i * 0.07, duration: 0.6, ease: EASE }}
                  onClick={() => runLink(l.action)}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="group flex items-center justify-between text-left border-b border-white/10 py-5"
                >
                  <span className="font-serif font-light text-3xl sm:text-4xl tracking-tight">
                    {l.label}
                  </span>
                  <ArrowUpRight size={22} className="text-[#C5A059] opacity-60 group-hover:opacity-100 transition-opacity" />
                </motion.button>
              ))}

              <motion.button
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 0.6, ease: EASE }}
                onClick={() => runLink(() => navigate("/request-concept"))}
                data-testid="mobile-nav-request-concept"
                className="mt-8 inline-flex items-center justify-center gap-3 bg-[#C5A059] text-[#1A1713] px-8 py-5 text-xs uppercase tracking-[0.2em] font-medium"
              >
                Request a Concept
                <ArrowUpRight size={16} />
              </motion.button>
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.6 }}
              className="px-6 sm:px-10 py-6 border-t border-white/10 flex flex-col gap-3 text-sm text-[#FDFBF7]/70"
            >
              <a href="tel:+918796953955" className="flex items-center gap-3" data-testid="mobile-menu-phone">
                <Phone size={15} className="text-[#C5A059]" /> +91 87969 53955
              </a>
              <a href="mailto:aurelumebranding@gmail.com" className="flex items-center gap-3" data-testid="mobile-menu-email">
                <Mail size={15} className="text-[#C5A059]" /> aurelumebranding@gmail.com
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
